import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { QUERY_KEYS } from '../queryClient';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ─────────────────────────────────────────────────────────────────────────────
// Hooks for the 3 "lazy" tabs (Use-cases, Competitions, GTM Accounts).
// Each:
//   • shares a single in-memory cache via TanStack Query
//   • returns isLoading so components can show a skeleton instead of "0"
//   • dedups in-flight requests automatically
//   • refetches in the background on window focus
// ─────────────────────────────────────────────────────────────────────────────

export function useUseCases() {
    return useQuery({
        queryKey: QUERY_KEYS.useCases,
        queryFn: async () => {
            const res = await api.getUseCases();
            return Array.isArray(res) ? res : [];
        }
    });
}

export function useCompetitions() {
    return useQuery({
        queryKey: QUERY_KEYS.competitions,
        queryFn: async () => {
            const r = await fetch(`${API_BASE}/competitions`);
            if (!r.ok) throw new Error(`competitions ${r.status}`);
            const data = await r.json();
            return Array.isArray(data) ? data : [];
        }
    });
}

export function useGTMAccounts() {
    return useQuery({
        queryKey: QUERY_KEYS.gtmAccounts,
        queryFn: async () => {
            // Endpoint now returns { rows, total, hasMore } for pagination
            // support. Older callers just want the flat array.
            const res = await api.getGTMAccounts();
            if (Array.isArray(res)) return res;              // legacy shape
            if (res && Array.isArray(res.rows)) return res.rows; // paginated shape
            return [];
        }
    });
}

// Prefetch helpers — fire-and-forget. Used by GICRM after bulkData hydrates
// so the user's first click on those tabs is a warm cache hit.
export function prefetchSecondaryTabs(queryClient) {
    const run = () => {
        queryClient.prefetchQuery({ queryKey: QUERY_KEYS.useCases,     queryFn: () => api.getUseCases() });
        queryClient.prefetchQuery({ queryKey: QUERY_KEYS.competitions, queryFn: async () => {
            const r = await fetch(`${API_BASE}/competitions`); return r.ok ? r.json() : [];
        }});
        queryClient.prefetchQuery({ queryKey: QUERY_KEYS.gtmAccounts,  queryFn: () => api.getGTMAccounts() });
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(run);
    } else {
        setTimeout(run, 1500);
    }
}
